import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import NumbersIcon from "@mui/icons-material/Numbers";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../../services/GoogleAnalytics";
import { decryptText } from "../../../services/CryptoUtil";
import { PUZZLE_GRID_COLORS } from "../../../services/ColorService";
import Logger from "../../../services/Logger";
import SlidingPlayMain from "../../../admin/story/SlidingPlayMain";
import { usePagesTranslation } from "../../locales";
import { fetchPublishedHowtoStory } from "../fetchPublishedHowtoStory";
import { getLocalizedHomeBrowseStoryTeaser } from "../../browse-puzzles/homePuzzleRoUtils";
import encryptedSampleFr from "./examples/sample_fr_sliding_encrypted.json";

const TOOLBAR_ICON_SX = { color: "#3BA666", fontSize: "1.35rem", mt: 0.25, flexShrink: 0 };

function Section({ title, body }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                {title}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                {body}
            </Typography>
        </Box>
    );
}

function ToolbarItem({ icon, label, body }) {
    return (
        <Box
            component="li"
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 1.75,
            }}
        >
            {icon}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.05rem", lineHeight: 1.7, mt: 0.25 }}>
                    {body}
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * How-to for sliding tile puzzles. Routed from `/howto/puzzle/sliding`.
 */
export default function HowToSliding() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [sampleData, setSampleData] = useState(null);
    const [exampleTileLook, setExampleTileLook] = useState("image");

    useEffect(() => {
        trackEvent("entered", { name: "HowToSliding" });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const encryptedText =
                    typeof encryptedSampleFr === "string"
                        ? encryptedSampleFr
                        : encryptedSampleFr?.data || encryptedSampleFr?.encrypted || Object.values(encryptedSampleFr)[0];
                if (!encryptedText || typeof encryptedText !== "string") return;
                const decrypted = decryptText(encryptedText.trim());
                if (!decrypted) return;
                const parsed = JSON.parse(decrypted);
                if (!cancelled) setSampleData(parsed);
                const liveStory = await fetchPublishedHowtoStory("SLIDING", "english");
                if (!cancelled && liveStory && parsed && typeof parsed === "object") {
                    setSampleData({
                        ...parsed,
                        story: { ...(parsed.story || {}), ...liveStory },
                    });
                }
            } catch (e) {
                Logger.error("HowToSliding: failed to decrypt sample", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const sliding = sampleData?.sliding ?? null;
    const story = sampleData?.story ?? null;
    const imageUrl = story?.imageUrl ?? story?.imagePreviewUrl ?? story?.imageIcon400Url ?? null;
    const exampleTeaser = getLocalizedHomeBrowseStoryTeaser({ story }, "english");

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                {t("howto.sliding.title")}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 3 }}>
                {t("howto.sliding.intro")}
            </Typography>

            {/* Example puzzle */}
            {sliding && imageUrl && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                        {t("howto.sliding.part-board-title")}
                    </Typography>
                    {exampleTeaser.storyTitle ? (
                        <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                            {exampleTeaser.storyTitle}
                        </Typography>
                    ) : null}
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                        <ToggleButtonGroup
                            exclusive
                            value={exampleTileLook}
                            onChange={(_, next) => {
                                if (next === "number" || next === "image") {
                                    setExampleTileLook(next);
                                }
                            }}
                            aria-label={t("howto.sliding.example-tile-look", "Number/Image")}
                            sx={{
                                "& .MuiToggleButton-root": {
                                    textTransform: "none",
                                    fontWeight: 700,
                                    gap: 0.75,
                                    px: 1.75,
                                    color: "#3BA666",
                                    borderColor: "rgba(59, 166, 102, 0.7)",
                                },
                                "& .MuiToggleButton-root.Mui-selected": {
                                    color: "#143024",
                                    backgroundColor: "#d7f0e2",
                                },
                                "& .MuiToggleButton-root.Mui-selected:hover": {
                                    backgroundColor: "#c5e8d4",
                                },
                            }}
                        >
                            <ToggleButton value="number" aria-label={t("howto.sliding.example-tile-number", "Number")}>
                                <NumbersIcon sx={{ fontSize: "1.2rem" }} />
                                {t("howto.sliding.example-tile-number", "Number")}
                            </ToggleButton>
                            <ToggleButton value="image" aria-label={t("howto.sliding.example-tile-image", "Image")}>
                                <ImageOutlinedIcon sx={{ fontSize: "1.2rem" }} />
                                {t("howto.sliding.example-tile-image", "Image")}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", md: "calc(50% - 8px)" },
                            mx: "auto",
                            aspectRatio: "1 / 1",
                            overflow: "hidden",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                        }}
                    >
                        <SlidingPlayMain
                            imageUrl={imageUrl}
                            gridSize={sliding.gridSize}
                            startingLayout={sliding.startingLayout}
                            hideCompletedCaption
                            fillParent
                            showTileNumbers={exampleTileLook === "number"}
                        />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate("/puzzles/sliding")}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor: PUZZLE_GRID_COLORS.BUTTON_PRIMARY_GREEN,
                                "&:hover": { backgroundColor: PUZZLE_GRID_COLORS.BUTTON_PRIMARY_GREEN_HOVER },
                            }}
                        >
                            {t("howto.sliding.browse-link")}
                        </Button>
                    </Box>
                </Box>
            )}

            <Section title={t("howto.sliding.part-reference-title")} body={t("howto.sliding.part-reference-body")} />
            {!sliding && <Section title={t("howto.sliding.part-board-title")} body={t("howto.sliding.part-board-body")} />}
            {sliding && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                        {t("howto.sliding.part-board-body")}
                    </Typography>
                </Box>
            )}
            <Section title={t("howto.sliding.part-levels-title")} body={t("howto.sliding.part-levels-body")} />

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                    {t("howto.sliding.part-toolbar-title")}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 2 }}>
                    {t("howto.sliding.part-toolbar-intro")}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                    <ToolbarItem
                        icon={<ArrowBackIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.toolbar-back-label")}
                        body={t("howto.sliding.toolbar-back-body")}
                    />
                    <ToolbarItem
                        icon={<IosShareIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.toolbar-share-label")}
                        body={t("howto.sliding.toolbar-share-body")}
                    />
                    <ToolbarItem
                        icon={<PauseIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.toolbar-timer-label")}
                        body={t("howto.sliding.toolbar-timer-body")}
                    />
                    <ToolbarItem
                        icon={
                            <Box
                                sx={{
                                    ...TOOLBAR_ICON_SX,
                                    width: 28,
                                    height: 22,
                                    borderRadius: 1,
                                    border: "1.5px solid #3BA666",
                                    fontSize: "0.65rem",
                                    fontWeight: 800,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    lineHeight: 1,
                                }}
                            >
                                12
                            </Box>
                        }
                        label={t("howto.sliding.toolbar-moves-label")}
                        body={t("howto.sliding.toolbar-moves-body")}
                    />
                    <ToolbarItem
                        icon={<LightbulbOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.toolbar-hint-label")}
                        body={t("howto.sliding.toolbar-hint-body")}
                    />
                    <ToolbarItem
                        icon={<SettingsIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.toolbar-settings-label")}
                        body={t("howto.sliding.toolbar-settings-body")}
                    />
                </Box>
                <Box component="ul" sx={{ listStyle: "none", m: 0, pl: { xs: 0, sm: 5 }, mt: 0.5 }}>
                    <ToolbarItem
                        icon={<AccountBalanceWalletOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.settings-balance-label")}
                        body={t("howto.sliding.settings-balance-body")}
                    />
                    <ToolbarItem
                        icon={<ImageOutlinedIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.settings-reference-label")}
                        body={t("howto.sliding.settings-reference-body")}
                    />
                    <ToolbarItem
                        icon={<MusicNoteIcon sx={TOOLBAR_ICON_SX} />}
                        label={t("howto.sliding.settings-audio-label")}
                        body={t("howto.sliding.settings-audio-body")}
                    />
                </Box>
            </Box>
        </Container>
    );
}
